import { LinearProgress } from '@mui/material';
import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import { RoleType } from '../clients/services';
import PageNotFound from '../ui/error/page-not-found';
import Guarded from './auth/guarded-component';
import Home from './modules/home';
import Logout from './modules/login/logout';
import Register from './modules/login/register';

const UserManagement = React.lazy(() => import(/* webpackChunkName: "user-management" */ './modules/user-management'));
const ModuleManagement = React.lazy(
  () => import(/* webpackChunkName: "module-management" */ './modules/module-management'),
);
const ModuleRegistrations = React.lazy(
  () => import(/* webpackChunkName: "module-registrations" */ './modules/module-registrations'),
);
const ProfileManagement = React.lazy(
  () => import(/* webpackChunkName: "profile-management" */ './modules/profile-management'),
);
const RegistrationImport = React.lazy(
  () => import(/* webpackChunkName: "registration-import" */ './modules/registration-import'),
);
const SemesterManagement = React.lazy(
  () => import(/* webpackChunkName: "semester-management" */ './modules/semester-management'),
);
const StudentManagement = React.lazy(
  () => import(/* webpackChunkName: "student-management" */ './modules/student-management'),
);
const UasManagement = React.lazy(() => import(/* webpackChunkName: "uas-management" */ './modules/uas-management'));
const AdminActions = React.lazy(() => import(/* webpackChunkName: "admin" */ './modules/admin'));
const ExamRegistrationsModule = React.lazy(
  () => import(/* webpackChunkName: "exam-registrations" */ './modules/exam-registrations'),
);

export const UserManagementRoute = '/user-management';
export const RegistrationImportRoute = '/registration-import';
export const ModuleRegistrationsRoute = '/module-registrations';
export const ModuleManagementRoute = '/module-management';
export const SemesterManagementRoute = '/semester-management';
export const ProfileManagementRoute = '/profile-management';
export const StudentManagementRoute = '/student-management';
export const UasManagementRoute = '/uas-management';
export const AdminRoute = '/admin';
export const ExamRegistrationsRoute = '/exam-registrations';

const AppRoutes = () => {
  const examRegistrationEnabled = useSelector((state: RootState) => state.application.examRegistrationEnabled);

  return (
    <Routes>
      <Route path="/logout" element={<Logout />} />
      <Route path="/register" element={<Register />} />
      <Route
        path={AdminRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.ADMIN, RoleType.USERADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <AdminActions baseUrl={AdminRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={UserManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.ADMIN, RoleType.USERADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <UserManagement baseUrl={UserManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={SemesterManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.PROCESSADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <SemesterManagement baseUrl={SemesterManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={ProfileManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.MODULEADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <ProfileManagement baseUrl={ProfileManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={ModuleManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.MODULEADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <ModuleManagement baseUrl={ModuleManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={UasManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.MASTEROFFICE, RoleType.REGISTRATIONADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <UasManagement baseUrl={UasManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={StudentManagementRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.STUDENTADMIN, RoleType.MASTEROFFICE, RoleType.REGISTRATIONADMIN]}>
            <Suspense fallback={<LinearProgress />}>
              <StudentManagement baseUrl={StudentManagementRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={RegistrationImportRoute + '/*'}
        element={
          <Guarded>
            <Suspense fallback={<LinearProgress />}>
              <RegistrationImport baseUrl={RegistrationImportRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={ModuleRegistrationsRoute + '/*'}
        element={
          <Guarded requiresRole={[RoleType.REGISTRATIONADMIN, RoleType.MASTEROFFICE]}>
            <Suspense fallback={<LinearProgress />}>
              <ModuleRegistrations baseUrl={ModuleRegistrationsRoute} />
            </Suspense>
          </Guarded>
        }
      />
      <Route
        path={ExamRegistrationsRoute + '/*'}
        element={
          examRegistrationEnabled
            ? (
              <Guarded requiresRole={[RoleType.REGISTRATIONADMIN, RoleType.MASTEROFFICE]}>
                <Suspense fallback={<LinearProgress />}>
                  <ExamRegistrationsModule baseUrl={ExamRegistrationsRoute} />
                </Suspense>
              </Guarded>
            )
            : <PageNotFound />
        }
      />
      <Route index element={<Home />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRoutes;
